import { FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

const WS_URL = process.env.REACT_APP_WS_URL;
const WS_PATH = process.env.REACT_APP_WS_PATH;

const DEFAULT_CLIENT_ID = 'demo-client';
const DEFAULT_AGENT_ID = 'leo';

type ChatMessage = {
  conversationId: string;
  messageId?: string;
  from: 'user' | 'agent' | string;
  text: string;
  timestamp?: string;
  metadata?: Record<string, unknown>;
};

type ThinkingPayload = { state?: 'start' | 'stop' | string };

function getOrCreateConversationId() {
  const key = 'demo_conversation_id';
  const existing = localStorage.getItem(key);
  if (existing) return existing;

  const id = `conv-${crypto.randomUUID?.() || String(Date.now())}`;
  localStorage.setItem(key, id);
  return id;
}

function newId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function SocketConnect() {
  const query = new URLSearchParams(window.location.search);
  const [clientId, setClientId] = useState(query.get('clientId') || DEFAULT_CLIENT_ID);
  const [agentId, setAgentId] = useState(query.get('agentId') || DEFAULT_AGENT_ID);
  const [socketError, setSocketError] = useState<string | null>(null);

  const [connected, setConnected] = useState(false);
  const [joined, setJoined] = useState(false);

  const [conversationId] = useState(() => getOrCreateConversationId());

  const [text, setText] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [thinking, setThinking] = useState(false);

  const [simulateSlow, setSimulateSlow] = useState(true);
  const [simulateDelayMs, setSimulateDelayMs] = useState(4500); // >4s triggers "Thinking…"

  const socketRef = useRef<Socket | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const startedRef = useRef(false);
  const firstResponseRef = useRef(false);
  const thinkingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastUserMessageIdRef = useRef<string | null>(null);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, thinking]);

  const socket = useMemo(() => {
    const s = io(WS_URL, {
      path: WS_PATH,
      transports: ['websocket'],
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 500,
      reconnectionDelayMax: 3000,
      auth: { clientId, agentId },
    });
    console.log('s', s)
    return s;
  }, [clientId, agentId]);

  console.log(socket, 'socket')

  async function track(name: string, extra: Record<string, unknown> = {}) {
    const s = socketRef.current;
    if (!s || !s.connected) return;

    const payload = {
      name,
      eventId: newId('evt'),
      timestamp: new Date().toISOString(),
      conversationId,
      ...extra,
    };

    try {
      await s.emitWithAck('analytics:track', payload);
    } catch (e) {
      console.error('analytics track failed:', e);
    }
  }

  useEffect(() => {
    socketRef.current = socket;

    const queuedWidgetLoaded = { done: false };

    const onConnect = async () => {
      setConnected(true);

      if (!queuedWidgetLoaded.done) {
        queuedWidgetLoaded.done = true;
        track('widget_loaded', { metadata: { agentId, ui: 'demo' } });
      }

      try {
        const res = await socket.emitWithAck('conversation:join', { conversationId });
        if (res?.ok) {
          setJoined(true);

          if (!startedRef.current) {
            startedRef.current = true;
            track('chat_started', {
              metadata: { agentId, ui: 'demo' },
            });
          }
        }
      } catch (e) {
        console.error('join failed', e);
      }
    };

    const onDisconnect = () => {
      setConnected(false);
      setJoined(false);
    };

    const onError = (payload: any) => {
      console.log(payload, 'payload here')
      console.error('socket error:', payload);
      if (payload?.code === 'NOT_ALLOWED') {
        setSocketError(
          `Connection not allowed for clientId "${payload?.clientId || clientId}" and agentId "${payload?.agentId || agentId}" from origin "${payload?.origin || window.location.origin}".`
        );
        return;
      }
      setSocketError(payload?.code ? `Socket error: ${payload.code}` : 'Socket error');
    };

    const onMessageNew = (msg: ChatMessage) => {
      if (msg?.from === 'agent') {
        if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
        thinkingTimerRef.current = null;
        setThinking(false);

        if (startedRef.current && !firstResponseRef.current) {
          firstResponseRef.current = true;
          track('first_response', {
            messageId: msg.messageId,
            metadata: { agentId, ui: 'demo' },
          });
        }
      }

      setMessages((prev) => {
        if (msg?.messageId && prev.some((m) => m.messageId === msg.messageId)) return prev;
        return [...prev, msg];
      });
    };

    const onThinking = (payload: ThinkingPayload) => {
      if (payload?.state === 'start') setThinking(true);
      if (payload?.state === 'stop') {
        if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
        thinkingTimerRef.current = null;
        setThinking(false);
      }
    };


    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('error', onError);
    socket.on('message:new', onMessageNew);
    socket.on('agent:thinking', onThinking);
    socket.onAny((event, ...args) => {
      console.log('[socket event]', event, args?.[0]);
    });
    socket.connect();

    const onBeforeUnload = () => {
      if (startedRef.current) {
        track('chat_ended', { metadata: { reason: 'unload', agentId, ui: 'demo' } });
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);

      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('error', onError);
      socket.off('message:new', onMessageNew);
      socket.off('agent:thinking', onThinking);

      socket.disconnect();
    };
  }, [socket, conversationId, simulateSlow, simulateDelayMs, agentId]);

  async function sendMessage(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const s = socketRef.current;
    if (!s || !s.connected) return;

    const trimmed = text.trim();
    if (!trimmed) return;

    const messageId = `m-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    lastUserMessageIdRef.current = messageId;

    const payload = {
      conversationId,
      messageId,
      from: 'user',
      text: trimmed,
      timestamp: new Date().toISOString(),
      metadata: { ui: 'demo' },
    };

    setMessages((prev) => [...prev, payload]);
    setText('');

    if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
    thinkingTimerRef.current = setTimeout(() => {
      setThinking(true);
    }, 4000);

    try {
      const ack = await s.emitWithAck('message:send', payload);
      if (!ack?.ok) {
        if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
        thinkingTimerRef.current = null;
        setThinking(false);
        alert(`Send failed: ${ack?.error || 'unknown'}`);
      }
    } catch (err) {
      if (thinkingTimerRef.current) clearTimeout(thinkingTimerRef.current);
      thinkingTimerRef.current = null;
      setThinking(false);
      alert('Send failed (exception)');
    }
  }

  return (
    <div style={{ maxWidth: 760, margin: '24px auto', fontFamily: 'system-ui, Arial' }}>
      <h2 style={{ marginBottom: 8 }}>WS Test</h2>

      {socketError && (
        <div
          style={{
            marginBottom: 12,
            background: '#FEF2F2',
            color: '#991B1B',
            border: '1px solid #FECACA',
            borderRadius: 8,
            padding: '10px 12px',
            fontSize: 13,
          }}
        >
          {socketError}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
        <span>
          Status:{' '}
          <b style={{ color: connected ? 'green' : 'crimson' }}>
            {connected ? 'Connected' : 'Disconnected'}
          </b>
        </span>
        <span style={{ opacity: 0.7, fontSize: 12 }}>
          conv: {conversationId} {joined ? '• joined' : '• not joined'}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'end' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, opacity: 0.7 }}>clientId</span>
          <input
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            style={{ padding: 8, width: 220 }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12, opacity: 0.7 }}>agentId</span>
          <input
            value={agentId}
            onChange={(e) => setAgentId(e.target.value)}
            style={{ padding: 8, width: 220 }}
          />
        </label>

        <button
          onClick={() => window.location.reload()}
          style={{ height: 38, padding: '0 12px' }}
          title="Reload to reconnect with new auth"
        >
          Reconnect
        </button>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 12 }}>
        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={simulateSlow}
            onChange={(e) => setSimulateSlow(e.target.checked)}
          />
          Simulate slow reply
        </label>

        <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          Delay (ms):
          <input
            type="number"
            value={simulateDelayMs}
            onChange={(e) => setSimulateDelayMs(Number(e.target.value))}
            style={{ width: 120, padding: 6 }}
            min={0}
          />
        </label>
      </div>

      <div
        style={{
          border: '1px solid #ddd',
          borderRadius: 10,
          padding: 12,
          height: 420,
          overflowY: 'auto',
          background: '#fafafa',
        }}
      >
        {messages.map((m) => (
          <div
            key={m.messageId || `${m.from}-${m.timestamp}-${Math.random()}`}
            style={{
              display: 'flex',
              justifyContent: m.from === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: 10,
            }}
          >
            <div
              style={{
                maxWidth: '80%',
                padding: '10px 12px',
                borderRadius: 12,
                background: m.from === 'user' ? '#dbeafe' : '#fff',
                border: '1px solid #e5e7eb',
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
                {m.from} • {m.timestamp ? new Date(m.timestamp).toLocaleTimeString() : ''}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{m.text}</div>
            </div>
          </div>
        ))}

        {thinking && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 10 }}>
            <div
              style={{
                maxWidth: '80%',
                padding: '10px 12px',
                borderRadius: 12,
                background: '#fff',
                border: '1px solid #e5e7eb',
                fontStyle: 'italic',
                opacity: 0.85,
              }}
            >
              Thinking…
            </div>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <form onSubmit={sendMessage} style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type a message…"
          style={{ flex: 1, padding: 10, borderRadius: 10, border: '1px solid #ddd' }}
        />
        <button
          type="submit"
          disabled={!connected || !joined}
          style={{ padding: '0 16px', borderRadius: 10 }}
        >
          Send
        </button>
      </form>

      <div style={{ marginTop: 10, fontSize: 12, opacity: 0.7 }}>
        Analytics events: <b>widget_loaded</b>, <b>chat_started</b>, <b>first_response</b>, <b>chat_ended</b>
      </div>
    </div>
  );
}